import { useEffect, useState } from "react";
//components
import { PageContainer } from "../components/ui/layout/PageContainer";
import { PageHeader } from "../components/ui/layout/PageHeader";
import Label from "../components/ui/labels/Label";
import { SelectContainer } from "../components/ui/layout/SelectContainer";
import Select from "react-select";
import { SpeedApprovalTable } from "@components/tables/SpeedApprovalTable";
//hooks
import useJobOptions from "../hooks/useJobOptions";
import useRunOptions from "../hooks/useRunOptions";
import { useUser } from "@contexts/UserContext";
import { useError } from "@contexts/ErrorContext";
import { useSpeedApprovals } from "@hooks/useSpeedApprovals";
//utils
import { fetchJobData } from "@utils/jobs/fetchJobData";
import { SpeedApprovalTooltip } from "@components/ui/tooltips/SpeedApprovalTooltips";

/**
 * User can view all pending or past speed approvals that they are allowed to approve. Won't show any speed approvals that they are not able to approve (would have to have speed notifications set up for the currently viewed job).
 * 
 * @returns {JSX.Element}
 * 
 * @category Pages
 */
export const SpeedNotificationApprovals = () => {
    const { jobNameOptions } = useJobOptions();
    const { runNameOptions, fetchRuns, clearRuns } = useRunOptions();
    const { storedJob, setStoredJob, user, userPermissions} = useUser()
    const {showError} = useError()

    const [selectedJobOption, setSelectedJobOption] = useState(null);
    const [selectedRunOption, setSelectedRunOption] = useState(null);

    const { speedApprovals, fetchSpeedApprovals, approve, reject } = useSpeedApprovals(selectedJobOption, selectedRunOption);

    const jobSelectedGetRuns = (job) => {
        fetchJobData(job, showError, setSelectedJobOption, setStoredJob)
        clearRuns();
        setSelectedRunOption(null);
    };

    useEffect(() => {
        if (selectedJobOption) {
            fetchRuns(selectedJobOption.value.toString());
        }
    }, [selectedJobOption]); //eslint-disable-line

    const runSelectedGetLiveTrackData = (run) => {
        setSelectedRunOption(run);
    };

  
    useEffect(() => {
        if(!selectedJobOption || !user?.UserID) return
        fetchSpeedApprovals(user.UserID);
    }, [user?.UserID, selectedJobOption, selectedRunOption]); //eslint-disable-line

    useEffect(() => {
        document.title = `MarTrak - Speed Notification Approvals`;
        
        //if a job was previously selected and added to UserContext, pull it in here
        if(storedJob){
            fetchJobData(storedJob, showError, setSelectedJobOption, setStoredJob)
        }
    }, []); //eslint-disable-line

  return (
    <>
      <PageHeader pageTitle="Speed Notification Approvals" />

      <PageContainer>

        {/* select job/run */}
        <Label>Select A Job</Label>

        <SelectContainer>
          <Select
            options={jobNameOptions}
            onChange={jobSelectedGetRuns}
            styles={{
              menuPortal: (base) => ({
                ...base,
                zIndex: 9999,
              }),
            }}
            menuPosition="fixed"
            value={selectedJobOption}
          />
        </SelectContainer>

        {/* run optional on this page, more of a filter */}
        <Label>Filter By Run</Label>

        <SelectContainer>
          <Select
            options={runNameOptions}
            onChange={runSelectedGetLiveTrackData}
            styles={{
              menuPortal: (base) => ({
                ...base,
                zIndex: 9999,
              }),
            }}
            menuPosition="fixed"
            value={selectedRunOption}
            isClearable
          />
        </SelectContainer>

        <div className="flex gap-2 items-center">
          <Label>
            Your Speed Approvals 
          </Label>

          <SpeedApprovalTooltip 
            canViewJobControl={userPermissions?.jobs.update}
          /> 
        </div>
          
        <SpeedApprovalTable
            rows={speedApprovals}
            onApprove={approve}
            onReject={reject}
            timezone={selectedJobOption?.timezone}
        />

      </PageContainer>
    </>
  );
};
